import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Calendar, Search, Heart, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useHapticFeedback } from '../../hooks/useHapticFeedback';

interface FABAction {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  onClick?: () => void;
  href?: string;
  color?: string;
}

interface FloatingActionButtonProps {
  actions?: FABAction[];
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({ actions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const { triggerHaptic } = useHapticFeedback();

  if (!isMobile) {
    return null;
  }

  const defaultActions: FABAction[] = [
    {
      icon: Calendar,
      label: 'Book Appointment',
      href: '/barbershops',
      color: 'bg-primary-500',
    },
    {
      icon: Search,
      label: 'Find Barbers',
      href: '/barbershops',
      color: 'bg-secondary-500',
    },
    {
      icon: Heart,
      label: 'Favorites',
      href: '/favorites',
      color: 'bg-pink-500',
    },
  ];

  const finalActions = actions || defaultActions;

  const toggleMenu = () => {
    triggerHaptic('light');
    setIsOpen(!isOpen);
  };

  return (
    <div className="fixed bottom-20 right-4 z-40" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Action Items */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col-reverse gap-3 mb-3"
          >
            {finalActions.map((action, index) => (
              <motion.div
                key={action.label}
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3"
              >
                <span className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 rounded-lg shadow-lg text-sm font-medium whitespace-nowrap">
                  {action.label}
                </span>
                {action.href ? (
                  <Link
                    to={action.href}
                    onClick={() => {
                      triggerHaptic('medium');
                      setIsOpen(false);
                    }}
                    className={`${action.color || 'bg-primary-500'} text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-shadow`}
                  >
                    <action.icon size={24} />
                  </Link>
                ) : (
                  <button
                    onClick={() => {
                      triggerHaptic('medium');
                      action.onClick?.();
                      setIsOpen(false);
                    }}
                    className={`${action.color || 'bg-primary-500'} text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-shadow`}
                  >
                    <action.icon size={24} />
                  </button>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main FAB Button */}
      <motion.button
        onClick={toggleMenu}
        className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-4 rounded-full shadow-xl hover:shadow-2xl transition-shadow"
        whileTap={{ scale: 0.9 }}
        animate={{ rotate: isOpen ? 45 : 0 }}
      >
        {isOpen ? <X size={28} /> : <Plus size={28} />}
      </motion.button>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleMenu}
            className="fixed inset-0 bg-black/20 -z-10"
            style={{ bottom: 0, top: 0 }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FloatingActionButton;
